import { action, atom, withAssign, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import {
  Array,
  AsyncStateN,
  BigNumber as BigNumber_,
  Order,
  pipe,
} from '@blade/std-lib'

import type { DataTableStateModel } from './reatom-data-table-state'
import type { Column } from '../ui/data-table-types'
import type { Ctx } from '@reatom/framework'

export type TableSortDirection = 'asc' | 'desc'

export type TableSorting<ColumnId extends string = string> = {
  sortColumn: ColumnId
  sortDirection: TableSortDirection
}

export type TableSortingOptions<ColumnId extends string = string> = {
  initialSorting?:
    | TableSorting<ColumnId>
    | ((ctx: Ctx) => TableSorting<ColumnId>)
}

type TableSortingArgs<
  D extends object,
  ColumnId extends string,
  Context extends object | undefined = undefined,
> = {
  columns: Array<Column<D, ColumnId, Context>>
  stateModel: DataTableStateModel<D>
}

export const reatomDataTableSorting = <
  D extends object,
  ColumnId extends string,
  Context extends object | undefined = undefined,
>({
  initialSorting,
  stateModel,
  columns,
}: TableSortingOptions<ColumnId> & TableSortingArgs<D, ColumnId, Context>) => {
  const sortingAtom = atom<TableSorting<ColumnId> | undefined>(
    undefined,
    'sortingAtom',
  ).pipe(
    withInit((ctx) => {
      if (typeof initialSorting === 'function') {
        return initialSorting(ctx)
      }
      return initialSorting
    }),
  )

  const applyClientSideSortingAction = action(async (ctx) => {
    const sorting = ctx.get(sortingAtom)

    const itemsState = ctx.get(stateModel)
    const items = (itemsState.value?.data ?? []) as Array<D>

    if (!sorting) {
      return
    }

    const cmp = getSortingCmp<ColumnId, D>(sorting, columns)

    const sortedItems = pipe(items, Array.sort(cmp))
    stateModel.setState(ctx, AsyncStateN.success({ data: sortedItems }))
  }, 'applyClientSideSortingAction')

  const toggleSortingAction = action((ctx, column: ColumnId) => {
    const sorting = ctx.get(sortingAtom)

    const initialSorting_ =
      typeof initialSorting === 'function'
        ? initialSorting(ctx)
        : initialSorting
    const nextSorting = ((): TableSorting<ColumnId> | undefined => {
      if (!sorting || sorting.sortColumn !== column) {
        return {
          sortColumn: column,
          sortDirection: 'asc',
        }
      }

      if (sorting.sortDirection === 'asc') {
        return {
          ...sorting,
          sortDirection: 'desc',
        }
      }

      if (!initialSorting_) {
        return undefined
      }

      // continue cycle if initial sorting is the same as the current column
      if (initialSorting_.sortColumn === column) {
        return {
          ...sorting,
          sortDirection: 'asc',
        }
      }
    })()

    return nextSorting
  }, 'toggleSortingAction')

  return sortingAtom.pipe(
    withAssign(() => ({
      applyClientSideSorting: applyClientSideSortingAction,
      toggleSorting: toggleSortingAction,
    })),
  )
}

const getSortingCmp = <ColId extends string, D extends object>(
  sorting: TableSorting<ColId>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Array<Column<any, ColId, any>>,
): Order.Order<D> => {
  const getSortingColumn = (item: D) => {
    const column = columns.find((c) => c.id === sorting.sortColumn)

    const itemValue = column?.getValue
      ? column.getValue(item)
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (item as any)[sorting.sortColumn]

    return itemValue
  }

  const cmpAsc = Order.mapInput(order, getSortingColumn)
  const cmpDesc = Order.flip(cmpAsc)

  const cmp = sorting.sortDirection === 'asc' ? cmpAsc : cmpDesc

  return cmp
}

const order: Order.Order<unknown> = (left: unknown, right: unknown) => {
  if (left instanceof BigNumber && right instanceof BigNumber) {
    return BigNumber_.order(left, right)
  }
  if (left instanceof Date && right instanceof Date) {
    return Order.Date(left, right)
  }
  if (typeof left === 'number' && typeof right === 'number') {
    return Order.Number(left, right)
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return Order.String(left, right)
  }
  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Order.Boolean(left, right)
  }
  if (left === right) {
    return 0
  }
  return (left as number) > (right as number) ? 1 : -1
}
